import * as React from 'react';
import * as styled from './styled';
import {Viewer} from 'components/Viewer';
import {useRoute} from 'react-router5';
import {RecipeIngredient, RecipeObject} from 'api/recipe/types';
import {getRecipe} from 'api/recipe/requests';
import {Ingredient} from './components/Ingredient';
import useLoading from 'hooks/use-loading';
import validateResponse from 'helpers/validate-response';
import isError from 'helpers/isError';
import {TotalEnergy} from '../TotalEnergy';

/**
 * The recipe viewer.
 * @return {React.ReactElement}
 * @constructor
 */
export default function RecipeViewer(): React.ReactElement {
  const {route} = useRoute();
  const {fetch, data, render} = useLoading<RecipeObject>();

  React.useEffect(() => {
    fetchRecipe(route.params.recipeId);
  }, [route.params.recipeId]);

  /**
   * Fetch the recipe.
   * @return {void}
   */
  function fetchRecipe(recipeId: RecipeObject['id']): void {
    fetch(() => new Promise((resolve) => (
      getRecipe(recipeId).then((response) => {
        validateResponse(response);

        if (response && !isError(response)) {
          resolve(response);
        }
      })
    )))
  }

  /**
   * Render content.
   * @return {JSX.Element}
   */
  function renderContent(): JSX.Element {
    /**
     * Render header.
     * @return {JSX.Element | undefined}
     */
    function renderHeader(): JSX.Element | undefined {
      return data ? (
        <Viewer.Header
          preview={data.preview}
          title={data.title}
          description={data.description}
        />
      ) : undefined;
    }

    /**
     * Render content.
     * @return {JSX.Element}
     */
    function renderContent(): JSX.Element {
      /**
       * Render ingredients.
       * @return {JSX.Element}
       */
      function renderIngredients(): JSX.Element {
        /**
         * Render ingredient
         * @return {JSX.Element}
         */
        function renderIngredient(
            ingredient: RecipeIngredient,
            index: number,
        ): JSX.Element {
          return (
            <Ingredient
              key={index}
              {...ingredient}
              index={index}
            />
          );
        }

        return data !== undefined ? (
          <React.Fragment>
            <styled.Header>
              <styled.Title>Ингредиенты</styled.Title>
              <TotalEnergy ingredients={data.ingredients} />
            </styled.Header>
            {data.ingredients.map(renderIngredient)}
          </React.Fragment>
        ) : <React.Fragment />;
      }

      return (
        <React.Fragment>
          {renderIngredients()}
        </React.Fragment>
      );
    }

    return (
      <Viewer>
        {{
          header: renderHeader(),
          content: renderContent(),
        }}
      </Viewer>
    );
  }

  /**
   * Render not found.
   * @return {JSX.Element}
   */
  function renderNotFound(): JSX.Element {
    return (
      <p>The recipe not found!</p>
    );
  }

  return render(renderContent, renderNotFound);
}
